/**
 * collection.js — FilterDrawer Web Component.
 *
 * Opens a filter/sort side-panel. The panel contains a native <form> that
 * submits via GET, so filtering works without JS.
 *
 * JS enhancements:
 *  - Drawer open/close animation
 *  - Collapsible filter groups
 *  - Sync sort_by from the URL into the form on open
 */
class FilterDrawer extends TRYComponent {
  /** @type {AbortController|null} */
  #controller = null;
  /** @type {FocusTrap|null} */
  #trap = null;

  connectedCallback() {
    super.connectedCallback();
    this.#controller = new AbortController();
    const { signal } = this.#controller;

    // Close on X, apply button, and overlay click
    this.querySelectorAll('[data-close]').forEach(el => el.addEventListener('click', () => this.close(), { signal }));
    this.querySelector('[data-overlay]')?.addEventListener('click', () => this.close(), { signal });

    // Escape key
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.getAttribute('aria-hidden') === 'false') this.close();
    }, { signal });

    // Open from any [data-filter-open] button on the page
    document.addEventListener('click', e => {
      if (e.target.closest('[data-filter-open]')) this.open();
    }, { signal });

    // Swipe-down-to-close on mobile (bottom sheet)
    const panel = this.querySelector('.filter-drawer__panel');
    if (panel) {
      swipeToDismiss({
        panel,
        isHandle: el => el.closest('.filter-drawer__indicator, .filter-drawer__header'),
        onDismiss: () => this.close(),
        signal,
      });
    }

    // Collapsible filter groups
    this.addEventListener('click', e => {
      if (e.target.closest('.filter-group__values')) return;
      const toggle = e.target.closest('[data-toggle]');
      if (!toggle) return;
      const group = toggle.closest('[data-filter-group], .filter-group');
      if (!group) return;
      const isOpen = group.hasAttribute('data-open');
      if (isOpen) {
        group.removeAttribute('data-open');
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        group.setAttribute('data-open', '');
        toggle.setAttribute('aria-expanded', 'true');
      }
    }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller?.abort();
    this.#trap?.release({ restoreFocus: false });
    // Avoid a stuck scroll lock if the section is re-rendered (e.g. theme editor) while open
    if (this.getAttribute('aria-hidden') === 'false') {
      document.body.removeAttribute('data-filter-open');
      document.body.style.overflow = '';
    }
  }

  open() {
    if (this.getAttribute('aria-hidden') === 'false') return;
    this.setAttribute('aria-hidden', 'false');
    document.body.setAttribute('data-filter-open', '');
    document.body.style.overflow = 'hidden';
    this.#trap = new FocusTrap(this);
    this.#trap.activate(this.querySelector('[data-close]'));
  }

  close() {
    this.setAttribute('aria-hidden', 'true');
    document.body.removeAttribute('data-filter-open');
    document.body.style.overflow = '';
    this.#trap?.release();
    this.#trap = null;
  }
}

customElements.define('filter-drawer', FilterDrawer);

/**
 * LoadMorePagination — AJAX load-more for collection and search pages.
 *
 * Fetches the next paginated URL, parses the HTML response, and appends
 * .product-card elements to [data-product-grid] without a full page reload.
 * Falls back to normal <a> link navigation if JS is unavailable or fails.
 */
class LoadMorePagination extends TRYComponent {
  #loading = false;

  connectedCallback() {
    super.connectedCallback();
    const btn = this.querySelector('[data-load-more]');
    if (!btn) return;
    btn.addEventListener('click', e => {
      e.preventDefault();
      this.#loadMore(btn);
    });
  }

  async #loadMore(btn) {
    if (this.#loading) return;

    const nextUrl = this.dataset.nextUrl;
    if (!nextUrl) return;

    this.#loading = true;

    btn.setAttribute('disabled', '');
    const originalText = btn.textContent.trim();
    btn.textContent = '…';

    try {
      const res = await fetch(nextUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const parser = new DOMParser();
      const newDoc = parser.parseFromString(await res.text(), 'text/html');

      // Append new product cards to the current grid
      const grid = document.querySelector('[data-product-grid]');
      if (!grid) throw new Error('LoadMorePagination: [data-product-grid] not found');
      newDoc.querySelectorAll('[data-product-grid] .product-card').forEach(card => {
        grid.appendChild(card);
      });

      // Update the count display ("24 av 311" → "48 av 311")
      const newCount  = newDoc.querySelector('.load-more__count');
      const thisCount = this.querySelector('.load-more__count');
      if (newCount && thisCount) thisCount.textContent = newCount.textContent;

      // Advance to next page URL, or hide component when exhausted
      const nextPagination = newDoc.querySelector('load-more-pagination');
      if (nextPagination?.dataset.nextUrl) {
        this.dataset.nextUrl = nextPagination.dataset.nextUrl;
        btn.href = nextPagination.dataset.nextUrl;
      } else {
        this.hidden = true;
      }

    } catch (err) {
      console.warn('[LoadMorePagination] fetch failed:', err);
    } finally {
      this.#loading = false;
      btn.removeAttribute('disabled');
      btn.textContent = originalText;
    }
  }
}

customElements.define('load-more-pagination', LoadMorePagination);

/**
 * collection-description — clamps a long collection description to a few lines
 * and reveals a "Read more" / "Read less" toggle when the text overflows.
 *
 * Progressive enhancement: the clamp class is added by JS, so without JS the
 * full description stays visible and the toggle button (hidden by default)
 * never appears.
 */
class CollectionDescription extends TRYComponent {
  static #CLAMPED = 'collection-header__description-text--clamped';

  static #DURATION = 320; // ms

  /** @type {HTMLElement|null} */ #text = null;
  /** @type {HTMLButtonElement|null} */ #toggle = null;
  /** @type {ResizeObserver|null} */ #resizeObserver = null;
  /** @type {Animation|null} */ #animation = null;
  #expanded = false;

  connectedCallback() {
    super.connectedCallback();
    this.#text = this.querySelector('[data-description-text]');
    this.#toggle = this.querySelector('[data-description-toggle]');
    if (!this.#text || !this.#toggle) return;

    this.#toggle.addEventListener('click', () => this.#onToggle());

    // Re-check overflow when the available width changes (only while collapsed
    // and not mid-animation).
    this.#resizeObserver = new ResizeObserver(() => {
      if (!this.#expanded && !this.#animation) this.#evaluate();
    });
    this.#resizeObserver.observe(this);

    this.#evaluate();

    // Web fonts can change line wrapping after the first measurement — re-check.
    document.fonts?.ready.then(() => {
      if (!this.#expanded) this.#evaluate();
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    this.#animation?.cancel();
    this.#animation = null;
  }

  // Apply the clamp, then show the toggle only if the text is actually truncated.
  #evaluate() {
    this.#text.classList.add(CollectionDescription.#CLAMPED);
    const overflows = this.#text.scrollHeight > this.#text.clientHeight + 1;
    this.#toggle.hidden = !overflows;
    if (!overflows) this.#text.classList.remove(CollectionDescription.#CLAMPED);
  }

  #onToggle() {
    if (this.#animation) return; // ignore clicks mid-animation
    this.#expanded ? this.#collapse() : this.#expand();
  }

  #expand() {
    const start = this.#text.clientHeight;            // clamped height
    this.#text.classList.remove(CollectionDescription.#CLAMPED);
    const end = this.#text.scrollHeight;              // full height
    this.#setState(true);
    this.#animateHeight(start, end, true);
  }

  #collapse() {
    const start = this.#text.scrollHeight;            // current full height
    this.#text.classList.add(CollectionDescription.#CLAMPED);
    const end = this.#text.clientHeight;              // clamped height
    this.#text.classList.remove(CollectionDescription.#CLAMPED);
    this.#setState(false);
    this.#animateHeight(start, end, false);
  }

  #setState(expanded) {
    this.#expanded = expanded;
    this.#toggle.setAttribute('aria-expanded', String(expanded));
    this.#toggle.textContent = expanded ? this.dataset.readLess : this.dataset.readMore;
  }

  // Animate the text box between two pixel heights, then settle to its natural
  // resting state (full height when expanded, clamped when collapsed).
  #animateHeight(start, end, expanding) {
    const settle = () => {
      this.#text.style.overflow = '';
      if (!expanding) this.#text.classList.add(CollectionDescription.#CLAMPED);
      this.#animation = null;
    };

    const reduced = matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced || start === end) {
      settle();
      return;
    }

    this.#text.style.overflow = 'hidden';
    this.#animation = this.#text.animate(
      [{ height: `${start}px` }, { height: `${end}px` }],
      { duration: CollectionDescription.#DURATION, easing: 'ease' }
    );
    this.#animation.onfinish = settle;
    this.#animation.oncancel = settle;
  }
}

customElements.define('collection-description', CollectionDescription);

function initCardAnimations(cards) {
  if (!cards.length) return;
  cards.forEach(c => c.classList.remove('is-visible'));

  let pending = cards.length;
  const observer = new IntersectionObserver((entries) => {
    entries
      .filter(e => e.isIntersecting)
      .sort((a, b) =>
        a.target.compareDocumentPosition(b.target) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      )
      .forEach((entry, i) => {
        entry.target.style.setProperty('--card-index', i);
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
        if (--pending === 0) observer.disconnect();
      });
  }, { threshold: 0.05 });

  cards.forEach(c => observer.observe(c));
}

// Init all non-skeleton product cards present at page load.
// Covers native collection, search, product recommendations, etc.
// Voyado pages start with skeleton cards only — filtered here;
// the MutationObserver below catches Voyado's real cards when they arrive.
const initialCards = [...document.querySelectorAll('.product-card')]
  .filter(c => !c.closest('.voyado-skeleton'));
if (initialCards.length) initCardAnimations(initialCards);

// Watch the whole document for dynamically-added cards (Voyado collection,
// recently-viewed, recommendations, load-more — all inject into different containers).
new MutationObserver(mutations => {
  const added = [];
  mutations.forEach(m =>
    m.addedNodes.forEach(n => {
      if (n.nodeType !== 1) return;
      if (n.classList.contains('product-card')) added.push(n);
      else n.querySelectorAll?.('.product-card').forEach(c => added.push(c));
    })
  );
  if (added.length) initCardAnimations(added);
}).observe(document.body, { childList: true, subtree: true });
